import { apiService } from './api';
import websocketService from './websocket';
import type { User, AuthResponse } from '../types/index';

export class AuthService {
  // Request login with Telegram username
  async requestLogin(telegramUsername: string): Promise<AuthResponse> {
    try {
      const response = await apiService.post<AuthResponse>('/auth/request-code', {
        telegram_username: telegramUsername,
      });
      
      return response.data || response as AuthResponse;
    } catch (error: any) {
      // Re-throw the error to preserve the original error details
      throw error;
    }
  }

  // Verify login with code
  async verifyLogin(telegramUsername: string, code: string): Promise<AuthResponse> {
    try {
      const response = await apiService.post<AuthResponse>('/auth/verify-code', {
        telegram_username: telegramUsername,
        verification_code: code,
      });
      
      const authData = response.data || response as AuthResponse;
      
      // Store token and user data if successful
      if (authData.success && authData.bearer_token) {
        apiService.setAuthToken(authData.bearer_token);
        if (authData.user) {
          localStorage.setItem('user', JSON.stringify(authData.user));
        }
        // Connect to WebSocket after successful login
        websocketService.connect(authData.bearer_token);
      }
      
      return authData;
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Verification failed'
      };
    }
  }

  // Verify current token
  async verifyToken(): Promise<{ user: User; valid: boolean }> {
    try {
      const response = await apiService.get('/auth/me') as any;
      
      // Backend returns {success: true, user: {...}} directly
      if (response.success && response.user) {
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(response.user));
        return { user: response.user, valid: true };
      }
      
      return { user: {} as User, valid: false };
    } catch (error) {
      return { user: {} as User, valid: false };
    }
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      // No logout endpoint in new API, just clear local data
    } catch (error) {
    } finally {
      // Clear local storage regardless of API call result
      apiService.clearAuthToken();
      localStorage.removeItem('user');
      // Disconnect WebSocket
      websocketService.disconnect();
    }
  }

  // Get stored user data
  getStoredUser(): User | null {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  }

  // Get stored token
  getStoredToken(): string | null {
    return apiService.getAuthToken();
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }
}

export const authService = new AuthService();
export default authService;